import { getAvailableTitles } from '@/arise/api';
import { Title } from '@/models/profile';

// For storing earned titles as IDs in local storage, per user id. Local storage is shared by every
// user who signs in through this browser instance, so titles are namespaced by their owner - the
// unkeyed legacy entry cannot be attributed to anyone, so it is dropped and refetched from the API.
export const LS_TITLES = 'ARISE_TITLES';
export const LS_TITLES_PREFIX = `${LS_TITLES}:`;

export function titlesStorageKey(userId: string) {
    return `${LS_TITLES_PREFIX}${userId}`;
}

export function addLocalUserTitle(userId: string, id: string): void {
    // Get the localstorage array of title IDs and add a new ID to it
    try {
        if (!userId || !id || typeof id !== 'string') return;

        const titles = getLocalUserTitleIds(userId);
        if (!titles.includes(id)) {
            titles.push(id);
            localStorage.setItem(titlesStorageKey(userId), JSON.stringify(titles));
        }
    } catch (error) {
        console.error('Could not add local title ', error);
    }
}

function getLocalUserTitleIds(userId: string): string[] {
    // Get the localstorage array of title IDs and return it
    try {
        if (!userId) return [];

        // Drop the pre-namespacing entry, its owner is unknown
        localStorage.removeItem(LS_TITLES);

        const storedTitles = localStorage.getItem(titlesStorageKey(userId));
        const titles = storedTitles ? JSON.parse(storedTitles) : [];
        return Array.isArray(titles) && titles.every((item) => typeof item === 'string') ? titles : [];
    } catch {
        return [];
    }
}

export function setLocalUserTitles(userId: string, titles: Title[]) {
    // Replace the local storage title IDs with a new array
    try {
        if (!userId) {
            console.warn('setLocalUserTitles: No user id given, not storing titles');
            return;
        }
        if (Array.isArray(titles) && titles.length) {
            // Convert array of titles to array of title IDs
            const ids = titles.map((title) => title.id);
            localStorage.setItem(titlesStorageKey(userId), JSON.stringify(ids));
        }
    } catch (error) {
        console.error('Could not set local user titles ', error);
    }
}

export async function getLocalUserTitles(userId: string): Promise<Title[]> {
    // Get local title IDs, hydrate them into full Titles and return as array
    try {
        const localIds = getLocalUserTitleIds(userId);
        if (!localIds.length) return [];

        const availableTitles = await getAvailableTitles();
        return availableTitles.filter((title) => localIds.includes(title.id));
    } catch (error) {
        return [];
    }
}

// Pass a user id to clear just that user's titles, or omit it to clear every stored set
export function clearLocalUserTitles(userId?: string) {
    try {
        localStorage.removeItem(LS_TITLES);

        if (userId) {
            localStorage.removeItem(titlesStorageKey(userId));
            return;
        }

        Object.keys(localStorage)
            .filter((key) => key.startsWith(LS_TITLES_PREFIX))
            .forEach((key) => localStorage.removeItem(key));
    } catch (error) {
        console.error('Could not clear local user titles ', error);
    }
}
